import {
    MediaPackProgram,
    MediaProgram,
    RbcReason,
} from '@calm/evexia-web-sdk';
import { ChevronRight } from '@mui/icons-material';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useLearnWhyDialogData } from '@rhg/hooks/use-learn-why';
import { LearnWhyProps, MatchingPod } from '@rhg/hooks/use-learn-why/types';
import { useScreeningResultsScales } from '@rhg/hooks/use-screening-results-scales';
import planMessages from '@rhg/lang/messages/plan';
import resultsMessages from '@rhg/lang/messages/results';
import {
    Dialog,
    Stack,
    Typography,
    Box,
    Container,
    Paper,
} from '@rhg/libs/material';
import { displayLocaleDateString } from '@rhg/utils/date-time';
import { routes } from '@rhg/utils/routes';
import {
    gadMaxScore,
    gadYAxis,
    phqMaxScore,
    phqYAxis,
} from '@rhg/utils/usercrypt/checkins';
import { parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import { useCallback, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { InfoAnimation } from '../animations/info-animation';
import { LoadingDots } from '../animations/loading-dots';
import { BottomAction } from '../bottom-action';
import { DialogCloseButton } from '../dialog-close-button';
import { Heading } from '../heading';
import { LinearGraph } from '../linear-graph';
import { MatchingChoicePod } from '../matching-choice-pod';
import { ProgramHeaderWrapper } from '../program/program-header/wrapper';
import { ScaleDialog } from '../results/scale-dialog';
import {
    styleBackground,
    styleCloseButton,
    styleContainer,
    styleContent,
    styleGraphWrapper,
    styleInfoIconWrapper,
} from './styles';

export interface LearnWhyDialogProps
    extends Partial<Pick<LearnWhyProps, 'isTopRecommendation' | 'packClass'>> {
    isOpen: boolean;
    onClose: () => void;
    packClass?: string;
    isTopRecommendation?: boolean;
    program?: MediaProgram;
    packProgram?: MediaPackProgram;
    reason?: RbcReason;
}

/**
 * A dialog explaining the user why a clinical program is recommended to them.
 */
export function LearnWhyDialog({
    isOpen,
    onClose,
    isTopRecommendation,
    packClass,
    program,
    packProgram,
    reason,
}: LearnWhyDialogProps) {
    const id = useId();
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile('md');
    const { push } = useRouter();
    const { gadScales, phqScales } = useScreeningResultsScales();
    const [isGadInfoDialogOpen, setIsGadInfoDialogOpen] = useState(false);
    const [isPhqInfoDialogOpen, setIsPhqInfoDialogOpen] = useState(false);

    const {
        anxietyPodData,
        depressionPodData,
        goalsPodData,
        topicsPodData,
        conditionsPodData,
        isLoading,
    } = useLearnWhyDialogData(reason);

    const handleGadInfoDialogOpen = useCallback(
        () => setIsGadInfoDialogOpen(true),
        []
    );
    const handleGadInfoDialogClose = useCallback(
        () => setIsGadInfoDialogOpen(false),
        []
    );
    const handlePhqInfoDialogOpen = useCallback(
        () => setIsPhqInfoDialogOpen(true),
        []
    );
    const handlePhqInfoDialogClose = useCallback(
        () => setIsPhqInfoDialogOpen(false),
        []
    );

    const isClinical = program?.isClinical;
    const contentType = isClinical
        ? formatMessage(planMessages['plan.learn_why_modal.program'])
        : formatMessage(planMessages['plan.learn_why_modal.content']);

    const headerTextAlign = isMobile ? 'center' : 'left';
    const onViewProgram = () => {
        if (program?.id && packClass) {
            push(routes.programDetails(packClass, program.id));
            onClose();
        }
    };

    const showAnxietyPodSubtitle =
        anxietyPodData?.entry.checkinDate && anxietyPodData.classification;

    const showDepressionPodSubtitle =
        depressionPodData?.entry.checkinDate &&
        depressionPodData.classification;

    const trackProps =
        program?.id && packClass
            ? { ProgramId: program?.id, PackClass: packClass }
            : undefined;
    return (
        <>
            <ScaleDialog
                trackDialog="GadScaleInfo"
                trackCloseButton="GadScaleInfoClose"
                isOpen={isGadInfoDialogOpen}
                onClose={handleGadInfoDialogClose}
                title={formatMessage(resultsMessages['results.gad_info.title'])}
                scales={gadScales}
            />
            <ScaleDialog
                trackDialog="PhqScaleInfo"
                trackCloseButton="PhqScaleInfoClose"
                isOpen={isPhqInfoDialogOpen}
                onClose={handlePhqInfoDialogClose}
                title={formatMessage(resultsMessages['results.phq_info.title'])}
                scales={phqScales}
            />
            <Dialog
                aria-labelledby={id}
                PaperProps={{ sx: styleBackground }}
                trackDialog="LearnWhyScreen"
                trackFeature="CarePlan"
                trackScreen="CarePlan"
                open={isOpen}
                onClose={onClose}
                fullScreen
            >
                <Box sx={styleContainer}>
                    <DialogCloseButton
                        trackButton="LearnWhyClose"
                        trackProps={trackProps}
                        isFullScreen
                        onClick={onClose}
                        sx={styleCloseButton}
                    />
                    <Box sx={styleInfoIconWrapper}>
                        <InfoAnimation />
                    </Box>
                    <ProgramHeaderWrapper
                        hideBackButton
                        programImageSrc={
                            packProgram?.image?.url ??
                            program?.backgroundImage?.url
                        }
                        contentMdSize={6}
                        withScrollEffect={false}
                    >
                        <Stack gap={4}>
                            <Typography
                                color="text.body"
                                variant="subtitle1"
                                textAlign={headerTextAlign}
                            >
                                {isTopRecommendation
                                    ? formatMessage(
                                          planMessages[
                                              'plan.learn_why_modal.top_recommendation'
                                          ]
                                      )
                                    : formatMessage(
                                          planMessages[
                                              'plan.learn_why_modal.additional_recommendations'
                                          ]
                                      )}
                            </Typography>
                            <Heading
                                title={program?.title ?? ''}
                                titleAlign={headerTextAlign}
                                titleVariant={isMobile ? 'h5' : 'h4'}
                                titleColor="text.body"
                                subtitle={formatMessage(
                                    planMessages[
                                        'plan.learn_why_modal.subtitle'
                                    ],
                                    {
                                        contentType,
                                    }
                                )}
                                subtitleAlign={headerTextAlign}
                            />
                        </Stack>
                    </ProgramHeaderWrapper>
                    <Container sx={styleContent}>
                        <Stack gap={6} mb={10}>
                            {isLoading && (
                                <Stack
                                    height={200}
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <LoadingDots />
                                </Stack>
                            )}
                            {anxietyPodData && (
                                <Paper elevation={0}>
                                    <Box sx={styleGraphWrapper}>
                                        <LinearGraph
                                            title={formatMessage(
                                                planMessages[
                                                    'plan.learn_why_modal.anxiety_pod.title'
                                                ]
                                            )}
                                            subtitle={
                                                showAnxietyPodSubtitle
                                                    ? formatMessage(
                                                          planMessages[
                                                              'plan.learn_why_modal.anxiety_pod.subtitle'
                                                          ],
                                                          {
                                                              date: displayLocaleDateString(
                                                                  parseISO(
                                                                      anxietyPodData
                                                                          ?.entry
                                                                          .checkinDate ??
                                                                          ''
                                                                  )
                                                              ),
                                                              level: formatMessage(
                                                                  resultsMessages[
                                                                      `results.label_lowercase.${anxietyPodData.classification}`
                                                                  ]
                                                              ),
                                                          }
                                                      )
                                                    : undefined
                                            }
                                            trackInfoButton="AnxietyHistoryInfo"
                                            onInfoClick={
                                                handleGadInfoDialogOpen
                                            }
                                            min={0}
                                            max={gadMaxScore}
                                            value={anxietyPodData.score}
                                            valueLabel={anxietyPodData.label}
                                            axis={gadYAxis}
                                        />
                                    </Box>
                                </Paper>
                            )}
                            {depressionPodData && (
                                <Paper elevation={0}>
                                    <Box sx={styleGraphWrapper}>
                                        <LinearGraph
                                            title={formatMessage(
                                                planMessages[
                                                    'plan.learn_why_modal.depression_pod.title'
                                                ]
                                            )}
                                            subtitle={
                                                showDepressionPodSubtitle
                                                    ? formatMessage(
                                                          planMessages[
                                                              'plan.learn_why_modal.depression_pod.subtitle'
                                                          ],
                                                          {
                                                              date: displayLocaleDateString(
                                                                  parseISO(
                                                                      depressionPodData
                                                                          ?.entry
                                                                          .checkinDate ??
                                                                          ''
                                                                  )
                                                              ),
                                                              level: formatMessage(
                                                                  resultsMessages[
                                                                      `results.label_lowercase.${depressionPodData.classification}`
                                                                  ]
                                                              ),
                                                          }
                                                      )
                                                    : undefined
                                            }
                                            trackInfoButton="DepressionHistoryInfo"
                                            onInfoClick={
                                                handlePhqInfoDialogOpen
                                            }
                                            min={0}
                                            max={phqMaxScore}
                                            value={depressionPodData.score}
                                            valueLabel={depressionPodData.label}
                                            axis={phqYAxis}
                                        />
                                    </Box>
                                </Paper>
                            )}
                            {conditionsPodData && (
                                <MatchingChoicePod
                                    {...conditionsPodData}
                                    type={MatchingPod.Conditions}
                                />
                            )}
                            {topicsPodData && (
                                <MatchingChoicePod
                                    {...topicsPodData}
                                    type={MatchingPod.Topics}
                                />
                            )}
                            {goalsPodData && (
                                <MatchingChoicePod
                                    {...goalsPodData}
                                    type={MatchingPod.Goals}
                                />
                            )}
                        </Stack>
                        <Typography variant="subtitle1" textAlign="center">
                            {formatMessage(
                                planMessages['plan.learn_why_modal.footer'],
                                {
                                    contentType,
                                }
                            )}
                        </Typography>
                    </Container>
                </Box>
                <BottomAction
                    trackButton={
                        isClinical ? 'LearnWhyViewProgram' : 'LearnWhyViewDtc'
                    }
                    trackProps={trackProps}
                    label={
                        isClinical
                            ? formatMessage(
                                  planMessages[
                                      'plan.learn_why_modal.view_program'
                                  ]
                              )
                            : formatMessage(
                                  planMessages['plan.learn_why_modal.view']
                              )
                    }
                    onClick={onViewProgram}
                    color="primary"
                    variant="contained"
                    endIcon={isClinical && <ChevronRight />}
                />
            </Dialog>
        </>
    );
}
